import { useState } from "react";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import Reveal from "@/components/base/Reveal";
import { useSeo } from "@/hooks/useSeo";
import { SITE } from "@/config/site";

const aboutJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE.name,
    url: SITE.baseUrl,
    logo: `${SITE.baseUrl}/vite.svg`,
    description: SITE.description,
    email: SITE.email,
    telephone: SITE.phone,
    address: { "@type": "PostalAddress", addressLocality: "北京", addressCountry: "CN" },
    contactPoint: {
      "@type": "ContactPoint",
      email: SITE.email,
      telephone: SITE.phone,
      contactType: "customer service",
      areaServed: "CN",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: SITE.baseUrl },
      { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE.baseUrl}/about` },
    ],
  },
];

export default function About() {
  useSeo({
    title: `关于抖漫_品牌介绍_联系我们 - 抖漫`,
    description: `了解抖漫，聚合短剧化动态漫画平台，竖屏全屏有声特效在线看。品牌介绍、平台荣誉与联系方式一应俱全，欢迎合作与反馈。`,
    keywords: `抖漫,抖漫品牌,抖漫关于我们,抖漫联系,动态漫画平台`,
    path: "/about",
    jsonLd: aboutJsonLd,
  });

  return (
    <Layout>
      {/* Hero */}
      <section className="relative pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Creative%20digital%20entertainment%20team%20workspace%20silhouette%20with%20glowing%20screens%20showing%20animated%20manhua%20artwork%2C%20dark%20cinematic%20atmosphere%2C%20warm%20orange%20and%20teal%20neon%20lighting%2C%20modern%20streaming%20company%20vibe%2C%20high%20detail%2C%20abstract%20and%20artistic&width=1600&height=900&seq=douman-about-hero&orientation=landscape"
            alt="抖漫品牌背景"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/80 via-background-950/60 to-background-950"></div>
        </div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-6">
          <Reveal>
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-heading text-4xl md:text-5xl text-foreground-50 mb-5 mt-4">
              关于<span className="text-primary-400">抖漫</span>
            </h1>
            <p className="text-base md:text-lg text-foreground-300 max-w-2xl leading-relaxed">
              抖漫是一个聚合短剧化动态漫画的内容平台，以竖屏全屏、自带配音与特效的沉浸式体验为核心，致力于让每一位观众都能轻松享受优质动态漫画。
            </p>
          </Reveal>
        </div>
      </section>

      {/* Brand intro */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div>
              <span className="text-sm text-primary-400 font-medium mb-2 block">品牌介绍</span>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-50 mb-5">
                让每一部动态漫画，都触手可及
              </h2>
              <div className="space-y-4 text-foreground-400 leading-relaxed">
                <p>
                  抖漫诞生于对内容消费趋势的深刻洞察。随着移动端竖屏内容成为主流，我们坚信动态漫画这一融合了画面、配音与特效的新兴内容形态，将带来前所未有的观看体验。
                </p>
                <p>
                  我们聚合了海量优质动态漫画作品，覆盖玄幻仙侠、都市异能、甜宠恋爱、悬疑惊悚等热门题材，全部采用竖屏全屏呈现，自带专业配音与沉浸式特效，让观众点开即看、追更不迷路。
                </p>
                <p>
                  从原创内容孵化到版权合作，从技术创新到社区运营，抖漫正不断拓展动态漫画的边界，为用户持续带来更多高质量、高沉浸的精彩作品。
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="relative overflow-hidden rounded-xl aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Team%20of%20artists%20collaborating%20on%20digital%20animation%20at%20modern%20studio%20with%20large%20screens%20showing%20colorful%20manhua%20frames%2C%20dark%20creative%20atmosphere%2C%20warm%20orange%20accent%20lighting%2C%20high%20detail%2C%20professional%20content%20creation&width=1200&height=900&seq=douman-about-intro&orientation=landscape"
                alt="抖漫创作团队"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Reputation / honors */}
      <section className="bg-background-900/60 border-y border-background-200/20 py-14 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <Reveal>
            <div className="text-center mb-10">
              <span className="text-sm text-primary-400 font-medium">平台荣誉</span>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-50 mt-2">
                值得信赖的口碑平台
              </h2>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              {[
                { num: "2000+", label: "海量作品", icon: "ri-film-line" },
                { num: "50亿+", label: "累计播放", icon: "ri-play-circle-line" },
                { num: "8000万+", label: "活跃用户", icon: "ri-user-heart-line" },
                { num: "9.5+", label: "平均评分", icon: "ri-star-line" },
              ].map((s, idx) => (
                <div
                  key={idx}
                  className="rounded-xl bg-background-800/80 border border-background-200/20 p-6 text-center"
                >
                  <span className="w-11 h-11 rounded-lg bg-primary-500/15 flex items-center justify-center text-primary-400 text-xl mb-3 mx-auto">
                    <i className={s.icon}></i>
                  </span>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-50">{s.num}</div>
                  <div className="text-sm text-foreground-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={160}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                {
                  icon: "ri-award-line",
                  title: "优质内容生态",
                  desc: "严选合作优质创作者与版权方，内容质量层层把关，只为呈现更好的动态漫画。",
                },
                {
                  icon: "ri-shield-check-line",
                  title: "版权合规保障",
                  desc: "坚持正版授权运营，尊重创作者权益，构建健康可持续的内容生态。",
                },
                {
                  icon: "ri-heart-3-line",
                  title: "用户口碑至上",
                  desc: "倾听每一位用户的声音，持续打磨产品体验，收获数千万观众的喜爱与信赖。",
                },
              ].map((c, idx) => (
                <div
                  key={idx}
                  className="rounded-xl bg-background-800/80 border border-background-200/20 p-6"
                >
                  <span className="w-11 h-11 rounded-lg bg-secondary-500/15 flex items-center justify-center text-secondary-400 text-xl mb-4">
                    <i className={c.icon}></i>
                  </span>
                  <h3 className="text-foreground-100 font-medium mb-2">{c.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{c.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Contact */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Contact info */}
          <Reveal>
            <div>
              <span className="text-sm text-primary-400 font-medium">联系我们</span>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-50 mt-2 mb-6">
                期待与你沟通
              </h2>
              <div className="space-y-4">
                {[
                  { icon: "ri-mail-line", label: "商务合作", value: "business@douman.app" },
                  { icon: "ri-customer-service-2-line", label: "客户服务", value: SITE.phone },
                  { icon: "ri-map-pin-line", label: "公司地址", value: SITE.address },
                  { icon: "ri-time-line", label: "工作时间", value: "周一至周日 9:00 - 21:00" },
                ].map((c, idx) => (
                  <div key={idx} className="flex items-start gap-3 rounded-xl bg-background-900/70 border border-background-200/20 p-4">
                    <span className="w-10 h-10 rounded-lg bg-primary-500/15 flex items-center justify-center text-primary-400 text-lg shrink-0">
                      <i className={c.icon}></i>
                    </span>
                    <div>
                      <div className="text-sm text-foreground-600">{c.label}</div>
                      <div className="text-foreground-100 font-medium mt-0.5">{c.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={120}>
            <div>
              <h3 className="font-heading text-xl md:text-2xl text-foreground-50 mb-6">给我们留言</h3>
              <ContactForm />
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}

function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // honeypot check
    const honeypot = (formData.get("website_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");
    try {
      const res = await fetch("https://readdy.ai/api/form/da08jr6th95mubjtue20", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后重试"
        );
      }
    } catch (err) {
      setStatus("error");
      setFormError("网络异常，请稍后重试");
    }
  };

  return (
    <form
      data-readdy-form="true"
      id="contact-form"
      onSubmit={handleSubmit}
      className="rounded-xl bg-background-900/70 border border-background-200/20 p-6"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="name" className="block text-sm text-foreground-400 mb-1.5">
            您的称呼
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            placeholder="请输入称呼"
            className="w-full px-4 py-3 rounded-md bg-background-800 border border-background-200/30 text-sm text-foreground-100 outline-none focus:border-primary-400 placeholder:text-foreground-600"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm text-foreground-400 mb-1.5">
            联系邮箱
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="w-full px-4 py-3 rounded-md bg-background-800 border border-background-200/30 text-sm text-foreground-100 outline-none focus:border-primary-400 placeholder:text-foreground-600"
          />
        </div>
      </div>
      <div className="mb-4">
        <label htmlFor="subject" className="block text-sm text-foreground-400 mb-1.5">
          咨询主题
        </label>
        <select
          id="subject"
          name="subject"
          className="w-full px-4 py-3 rounded-md bg-background-800 border border-background-200/30 text-sm text-foreground-100 outline-none focus:border-primary-400"
        >
          <option value="商务合作">商务合作</option>
          <option value="版权授权">版权授权</option>
          <option value="内容建议">内容建议</option>
          <option value="技术问题">技术问题</option>
          <option value="其他">其他</option>
        </select>
      </div>
      <div className="mb-4">
        <label htmlFor="message" className="block text-sm text-foreground-400 mb-1.5">
          留言内容
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={5}
          maxLength={500}
          placeholder="请描述您的需求或反馈，最多500字"
          className="w-full px-4 py-3 rounded-md bg-background-800 border border-background-200/30 text-sm text-foreground-100 outline-none focus:border-primary-400 placeholder:text-foreground-600 resize-none"
        />
      </div>

      {/* honeypot */}
      <div className="hp-field" aria-hidden="true">
        <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly />
      </div>

      {status === "success" && (
        <div className="mb-4 px-4 py-3 rounded-md bg-secondary-500/15 border border-secondary-500/40 text-secondary-400 text-sm">
          <i className="ri-checkbox-circle-line mr-1"></i>
          提交成功，我们会尽快与您联系！
        </div>
      )}
      {status === "error" && (
        <div className="mb-4 px-4 py-3 rounded-md bg-accent-500/15 border border-accent-500/40 text-accent-400 text-sm">
          <i className="ri-error-warning-line mr-1"></i>
          {formError}
        </div>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="w-full px-6 py-3 rounded-md bg-primary-500 text-background-950 text-base font-medium whitespace-nowrap hover:bg-primary-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "submitting" ? "提交中…" : "提交留言"}
      </button>
    </form>
  );
}